import { useEffect, useState } from "react";
import {
  Settings as SettingsIcon,
  Users,
  Plus,
  Save,
  X,
  ShieldCheck,
  Building2,
  Mail,
  FileText,
  ListTodo,
  Calculator,
  HardHat,
  Sparkles,
  Trash2,
  KeyRound,
  BriefcaseBusiness,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { authApi } from "../lib/auth";
import { ChangePasswordModal } from "../components/ChangePasswordModal";
import GoogleLoginSettings from "../components/GoogleLoginSettings";
import FieldAccessSettings from "../components/FieldAccessSettings";
import { MENU_PERMISSIONS, menuKey, canMenu, permissionChoice, withPermissionChoice } from "../lib/menuPermissions";

const ROLE_PRESETS = {
  admin: { label: "Amministratore", is_admin: true, all_sites: true, permissions: { sites:true,tasks:true,emails:true,documents:true,offers:true,accounting:true,workers:true,ai:true,admin:true,write:true } },
  responsabile: { label: "Responsabile operativo", is_admin: false, all_sites: false, permissions: { sites:true,tasks:true,emails:true,documents:true,offers:true,accounting:true,workers:true,ai:true,admin:false,write:true } },
  collaboratore: { label: "Collaboratore", is_admin: false, all_sites: false, permissions: { sites:true,tasks:true,emails:false,documents:true,offers:false,accounting:false,workers:false,ai:true,admin:false,write:true } },
  consultazione: { label: "Consultazione", is_admin: false, all_sites: false, permissions: { sites:true,tasks:true,emails:true,documents:true,offers:true,accounting:true,workers:true,ai:false,admin:false,write:false } },
};

const emptyUser = {
  email: "",
  name: "",
  active: true,
  is_admin: false,
  all_sites: false,
  site_ids: [],
  permissions: {},
};

const WhatsAppQr = ({onReady}) => {
  const [qr,setQr]=useState(null);
  useEffect(()=>{let alive=true;const poll=async()=>{try{const q=await api.whatsappQr();if(!alive)return;setQr(q.qrDataUrl||null);if(q.status==="ready")onReady?.()}catch{}};poll();const t=setInterval(poll,3000);return()=>{alive=false;clearInterval(t)}},[onReady]);
  return <div className="w-52 shrink-0 text-center">{qr?<img src={qr} alt="QR WhatsApp Web" className="w-52 h-52 border rounded-xl p-2 bg-white"/>:<div className="w-52 h-52 border rounded-xl flex items-center justify-center text-xs text-slate-400">Attendo QR…</div>}<div className="text-[11px] text-slate-500 mt-2">WhatsApp → Dispositivi collegati → Collega dispositivo</div></div>;
};

export default function Settings({session}) {
  const isAdmin = Boolean(session?.user?.is_admin);
  const settingKeys = {azienda:"company",utenti:"users",field:"field",integrazioni:"integrations",ai:"ai"};
  const canSetting = key => key === "sicurezza" ? isAdmin : canMenu(session?.user,"settings",settingKeys[key]) && (key === "azienda" || isAdmin);
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [googleAccount, setGoogleAccount] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [company, setCompany] = useState(null);
  const [companyBusy, setCompanyBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [aiUsage, setAiUsage] = useState(null);
  const [openaiSettings, setOpenaiSettings] = useState(null);
  const [openrouterSettings,setOpenrouterSettings]=useState(null);
  const [openrouterKey,setOpenrouterKey]=useState("");
  const [openrouterVisible,setOpenrouterVisible]=useState(false);
  const [openrouterBusy,setOpenrouterBusy]=useState(false);
  const [openrouterTesting,setOpenrouterTesting]=useState(false);
  const [openaiKey, setOpenaiKey] = useState("");
  const [openaiKeyVisible, setOpenaiKeyVisible] = useState(false);
  const [openaiBusy, setOpenaiBusy] = useState(false);
  const [openaiTestBusy, setOpenaiTestBusy] = useState(false);
  const [openaiBalance, setOpenaiBalance] = useState("");
  const [openaiAdminKey, setOpenaiAdminKey] = useState("");
  const [openaiAdminVisible, setOpenaiAdminVisible] = useState(false);
  const [openaiAdminBusy, setOpenaiAdminBusy] = useState(false);
  const [whatsapp, setWhatsapp] = useState(null);
  const [whatsappChannels, setWhatsappChannels] = useState([]);
  const [whatsappSyncBusy, setWhatsappSyncBusy] = useState(false);
  const [whatsappToggleBusy, setWhatsappToggleBusy] = useState(false);
  const [section, setSection] = useState("azienda");

  const load = async () => {
    try {
      const [u, s, c] = await Promise.all([
        canSetting("utenti") ? api.listUsers() : Promise.resolve([]),
        (canSetting("utenti") || canSetting("field")) ? api.siteOptions() : Promise.resolve([]),
        canSetting("azienda") ? api.getCompanyProfile() : Promise.resolve(null),
      ]);
      setUsers(u);
      setSites(s);
      setCompany(c);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile caricare gli utenti");
    }
  };

  useEffect(() => {
    load();
    if (canSetting("integrazioni")) authApi.googleAccount().then(setGoogleAccount).catch(() => setGoogleAccount(null));
    if (canSetting("ai")) api.aiUsage().then(setAiUsage).catch(() => setAiUsage(null));
    if (canSetting("ai")) api.openaiSettings().then(setOpenaiSettings).catch(() => setOpenaiSettings(null));
    if (canSetting("ai")) api.openrouterSettings().then(setOpenrouterSettings).catch(() => setOpenrouterSettings(null));
    if (canSetting("integrazioni")) api.whatsappStatus().then(setWhatsapp).catch(()=>setWhatsapp(null));
  }, []);

  const loadWhatsappChannels = async () => {
    try { setWhatsappChannels(await api.whatsappChannels()); }
    catch { setWhatsappChannels([]); }
  };

  useEffect(() => {
    if (whatsapp?.status === "ready") loadWhatsappChannels();
    else setWhatsappChannels([]);
  }, [whatsapp?.status]);

  useEffect(() => {
    if (!canSetting(section)) setSection(Object.keys(settingKeys).find(canSetting) || "none");
  }, [session, section]);

  const openNew = () => setEditing({ ...emptyUser });

  const openEdit = (u) =>
    setEditing({
      ...u,
      site_ids: u.site_ids || [],
      permissions: u.permissions || {},
    });

  const toggleSite = (id) => {
    setEditing((u) => ({
      ...u,
      site_ids: u.site_ids.includes(id)
        ? u.site_ids.filter((x) => x !== id)
        : [...u.site_ids, id],
    }));
  };

  const save = async () => {
    if (!editing.email?.trim()) {
      toast.error("Inserisci l'email Google autorizzata");
      return;
    }

    setBusy(true);

    try {
      const payload = {
        email: editing.email.trim().toLowerCase(),
        name: editing.name || "",
        active: !!editing.active,
        is_admin: !!editing.is_admin,
        all_sites: !!editing.all_sites,
        site_ids: editing.all_sites ? [] : editing.site_ids || [],
        permissions: editing.permissions || {},
      };

      if (editing.id) {
        await api.updateUser(editing.id, payload);
      } else {
        await api.createUser(payload);
      }

      toast.success(editing.id ? "Utente aggiornato" : "Utente autorizzato");
      setEditing(null);
      window.dispatchEvent(new Event("edil:permissions-changed"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setBusy(false);
    }
  };

  const disable = async (u) => {
    if (!window.confirm(`Disattivare ${u.email}?`)) return;

    try {
      await api.deleteUser(u.id);
      toast.success("Utente disattivato");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operazione non riuscita");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-2">
            <SettingsIcon className="w-7 h-7 text-orange-600" />
            <h1 className="text-3xl font-display font-extrabold text-slate-900">
              Impostazioni
            </h1>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Configurazione aziendale, utenti, integrazioni, AI e sicurezza.
          </p>
        </div>

        {section === "utenti" && <button
          onClick={openNew}
          className="sm:ml-auto inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold px-4 py-2.5 rounded-xl"
        >
          <Plus className="w-4 h-4" />
          Aggiungi utente
        </button>}
      </div>
      <div className="overflow-x-auto pb-1">
        <div className="inline-flex min-w-max bg-white border border-slate-200 rounded-xl p-1 shadow-sm">
          {[
            ["azienda","Azienda",BriefcaseBusiness],
            ["utenti","Utenti e permessi",Users],
            ["field","Field",ShieldCheck],
            ["integrazioni","Integrazioni",Mail],
            ["ai","AI e consumi",Sparkles],
            ["sicurezza","Sicurezza",ShieldCheck],
          ].filter(([key]) => canSetting(key)).map(([key,label,Icon]) => <button key={key} onClick={()=>setSection(key)} className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold transition-colors ${section===key?"bg-slate-900 text-white":"text-slate-600 hover:bg-slate-50"}`}><Icon className="w-4 h-4"/>{label}</button>)}
        </div>
      </div>

      {section === "field" && <FieldAccessSettings sites={sites} />}

      {section === "ai" && <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
        <div className="flex gap-2 items-center"><KeyRound className="w-5 h-5 text-orange-600"/><h2 className="font-semibold">OpenRouter API — modelli AI gratuiti</h2>
          {openrouterSettings?.configured&&<span className="text-xs rounded-full px-2 py-1 bg-green-50 text-green-700">Configurata</span>}</div>
        <p className="text-sm text-slate-500">Chiave server per i modelli OpenRouter selezionati nell'AI Project Engine. Non viene mai restituita per intero. I modelli free possono avere limiti di utilizzo e richiedono attenzione alla riservatezza dei documenti.</p>
        <p className="text-xs text-slate-500">{openrouterSettings?.configured?`Chiave attiva: ${openrouterSettings.masked_key} · origine ${openrouterSettings.source}`:"Nessuna chiave OpenRouter configurata."}</p>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[260px] max-w-2xl">
            <input type={openrouterVisible?"text":"password"} value={openrouterKey} onChange={e=>setOpenrouterKey(e.target.value)}
              autoComplete="new-password" placeholder="sk-or-v1-..." className="w-full border border-slate-200 rounded-lg px-3 py-2.5 pr-24 font-mono text-sm"/>
            <button type="button" onClick={()=>setOpenrouterVisible(v=>!v)} className="absolute right-2 top-2 px-2 py-1 text-xs text-slate-500">{openrouterVisible?"Nascondi":"Mostra"}</button>
          </div>
          <button type="button" disabled={openrouterBusy||!openrouterKey.trim()} onClick={async()=>{
            setOpenrouterBusy(true);try{const result=await api.updateOpenrouterSettings(openrouterKey.trim());setOpenrouterSettings(result);setOpenrouterKey("");setOpenrouterVisible(false);toast.success("Chiave OpenRouter salvata")}
            catch(e){toast.error(e?.response?.data?.detail||"Impossibile salvare OpenRouter")}finally{setOpenrouterBusy(false)}
          }} className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50"><Save className="w-4 h-4 inline mr-1"/>Salva chiave</button>
          <button type="button" disabled={!openrouterSettings?.configured||openrouterTesting} onClick={async()=>{
            setOpenrouterTesting(true);try{const result=await api.testOpenrouterSettings();result.valid?toast.success(result.message):toast.error(result.message)}
            catch(e){toast.error(e?.response?.data?.detail||"Test OpenRouter non disponibile")}finally{setOpenrouterTesting(false)}
          }} className="border border-slate-300 rounded-lg px-4 py-2.5 text-sm disabled:opacity-50">Verifica connessione</button>
        </div>
        <a href="https://openrouter.ai/settings/keys" target="_blank" rel="noreferrer" className="text-xs text-orange-700 underline">Gestisci le chiavi OpenRouter</a>
      </div>}

      {section === "ai" && <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-violet-600"/>
              <div className="font-semibold text-slate-900">Chiave API OpenAI</div>
              {openaiSettings?.configured && <span className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">Configurata</span>}
            </div>
            <div className="text-sm text-slate-500 mt-1">
              {openaiSettings?.configured
                ? <>Chiave attiva: <span className="font-mono">{openaiSettings.masked_key}</span> · origine {openaiSettings.source === "settings" ? "Impostazioni" : "runtime server"}</>
                : "Nessuna chiave OpenAI configurata."}
            </div>
            <div className="relative mt-3 max-w-2xl">
              <input type={openaiKeyVisible ? "text" : "password"} value={openaiKey} onChange={(e)=>setOpenaiKey(e.target.value)}
                placeholder="sk-proj-… oppure sk-…"
                autoComplete="new-password"
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 pr-24 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30"/>
              <button type="button" onClick={()=>setOpenaiKeyVisible(v=>!v)} className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-xs text-slate-500 hover:text-slate-800">
                {openaiKeyVisible ? "Nascondi" : "Mostra"}
              </button>
            </div>
            <div className="text-xs text-slate-400 mt-2">La chiave salvata non viene mai mostrata integralmente dall'app. Inserisci una nuova chiave solo quando vuoi sostituire quella attiva.</div>
          </div>
          <button disabled={openaiBusy || !openaiKey.trim()} onClick={async()=>{
            setOpenaiBusy(true);
            try {
              const saved = await api.updateOpenaiSettings(openaiKey.trim());
              setOpenaiSettings(saved);
              setOpenaiKey("");
              setOpenaiKeyVisible(false);
              if (canSetting("ai")) api.aiUsage().then(setAiUsage).catch(()=>{});
              toast.success("Chiave OpenAI aggiornata e attivata");
            } catch(e) {
              toast.error(e?.response?.data?.detail || "Aggiornamento chiave OpenAI non riuscito");
            } finally { setOpenaiBusy(false); }
          }} className="inline-flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-semibold px-4 py-2.5 rounded-lg disabled:opacity-50">
            <Save className="w-4 h-4"/> {openaiBusy ? "Salvataggio…" : "Salva nuova chiave"}
          </button>
        </div>
      </div>}

      {section === "ai" && <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-blue-600"/>
              <div className="font-semibold text-slate-900">OpenAI Platform Admin API Key</div>
              {openaiSettings?.admin_configured && <span className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">Configurata</span>}
            </div>
            <div className="text-sm text-slate-500 mt-1">
              Serve esclusivamente per leggere i costi ufficiali dell'organizzazione. Non viene usata per le chiamate AI.
              {openaiSettings?.admin_configured && <> Chiave attiva: <span className="font-mono">{openaiSettings.masked_admin_key}</span>.</>}
            </div>
            <div className="relative mt-3 max-w-2xl">
              <input type={openaiAdminVisible ? "text" : "password"} value={openaiAdminKey} onChange={(e)=>setOpenaiAdminKey(e.target.value)}
                placeholder="Incolla la Platform Admin API Key"
                autoComplete="new-password"
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 pr-24 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"/>
              <button type="button" onClick={()=>setOpenaiAdminVisible(v=>!v)} className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-xs text-slate-500 hover:text-slate-800">
                {openaiAdminVisible ? "Nascondi" : "Mostra"}
              </button>
            </div>
            <div className="text-xs text-slate-400 mt-2">Creala nella Platform OpenAI come chiave amministratore dell'organizzazione. La chiave viene validata sui costi prima di essere salvata.</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={openaiSettings?.admin_key_url || "https://platform.openai.com/settings/organization/admin-keys"} target="_blank" rel="noreferrer" className="px-3 py-2.5 text-sm font-semibold border border-slate-200 rounded-lg bg-white">Crea Admin key</a>
            <button disabled={openaiAdminBusy || !openaiAdminKey.trim()} onClick={async()=>{
              setOpenaiAdminBusy(true);
              try {
                await api.updateOpenaiAdminSettings(openaiAdminKey.trim());
                setOpenaiAdminKey(""); setOpenaiAdminVisible(false);
                const pair=await Promise.all([api.openaiSettings(),api.aiUsage()]);
                setOpenaiSettings(pair[0]); setAiUsage(pair[1]);
                toast.success("Admin API key verificata e salvata");
              } catch(e) {
                toast.error(e?.response?.data?.detail || "Admin API key non valida o senza permessi sui costi");
              } finally { setOpenaiAdminBusy(false); }
            }} className="px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50">
              {openaiAdminBusy ? "Verifica…" : "Verifica e salva"}
            </button>
          </div>
        </div>
      </div>}

      {section === "ai" && aiUsage && <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Sparkles className="w-5 h-5 text-violet-600"/>
          <div><div className="font-semibold text-slate-900">Consumi AI</div><div className="text-sm text-slate-500">Utilizzo OpenAI registrato da Ellebierre · mese {aiUsage.month}</div></div>
          <span className={"ml-auto text-xs font-semibold px-2.5 py-1 rounded-full border "+(aiUsage.status==="quota_exhausted"?"bg-red-50 text-red-700 border-red-200":aiUsage.status==="operational"?"bg-emerald-50 text-emerald-700 border-emerald-200":"bg-amber-50 text-amber-700 border-amber-200")}>{aiUsage.status==="quota_exhausted"?"Credito esaurito":aiUsage.status==="operational"?"API operativa":"Stato da verificare"}</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-500">Chiamate</div><div className="text-xl font-bold">{aiUsage.total?.calls||0}</div></div>
          <div className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-500">Token input</div><div className="text-xl font-bold">{(aiUsage.total?.input_tokens||0).toLocaleString("it-IT")}</div></div>
          <div className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-500">Token output</div><div className="text-xl font-bold">{(aiUsage.total?.output_tokens||0).toLocaleString("it-IT")}</div></div>
          <div className="bg-violet-50 rounded-xl p-3"><div className="text-xs text-violet-600">Spesa stimata mese</div><div className="text-xl font-bold text-violet-800">$ {Number(aiUsage.total?.estimated_cost_usd||0).toFixed(6)}</div></div>
          <div className="bg-blue-50 rounded-xl p-3"><div className="text-xs text-blue-700">Costo ufficiale OpenAI MTD</div><div className="text-xl font-bold text-blue-900">{aiUsage.official_cost_mtd_usd == null ? "—" : "$ "+Number(aiUsage.official_cost_mtd_usd).toFixed(6)}</div>{aiUsage.official_cost_error && <div className="text-[10px] text-red-600 mt-1 line-clamp-2">{aiUsage.official_cost_error}</div>}</div>
          <div className="bg-emerald-50 rounded-xl p-3">
            <div className="text-xs text-emerald-700">Residuo stimato Ellebierre</div>
            <div className="text-xl font-bold text-emerald-800">{aiUsage.estimated_remaining_usd == null ? "—" : "$ "+Number(aiUsage.estimated_remaining_usd).toFixed(4)}</div>
            {aiUsage.reference_balance_usd != null && <div className="text-[10px] text-emerald-700 mt-1">da saldo riferimento $ {Number(aiUsage.reference_balance_usd).toFixed(2)}</div>}
          </div>
        </div>

        <div className="mt-4 border rounded-xl p-4 bg-slate-50/60">
          <div className="flex flex-col lg:flex-row gap-4 lg:items-end">
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-800">Saldo crediti OpenAI</div>
              <div className="text-xs text-slate-500 mt-1">Il saldo prepagato ufficiale non è esposto dalla normale API OpenAI. Inserisci qui il saldo che leggi nel portale Billing per stimare il residuo sottraendo i consumi registrati da Ellebierre.</div>
              <div className="flex gap-2 mt-3 max-w-md">
                <input type="number" min="0" step="0.01" value={openaiBalance} onChange={(e)=>setOpenaiBalance(e.target.value)} placeholder={aiUsage.reference_balance_usd != null ? String(aiUsage.reference_balance_usd) : "es. 20.00"} className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm"/>
                <button disabled={!openaiBalance} onClick={async()=>{
                  try {
                    await api.updateOpenaiBalanceReference(Number(openaiBalance));
                    setOpenaiBalance("");
                    const pair = await Promise.all([api.aiUsage(), api.openaiSettings()]);
                    setAiUsage(pair[0]); setOpenaiSettings(pair[1]);
                    toast.success("Saldo di riferimento aggiornato");
                  } catch(e) { toast.error(e?.response?.data?.detail || "Aggiornamento saldo non riuscito"); }
                }} className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm font-semibold disabled:opacity-50">Imposta saldo</button>
              </div>
              {aiUsage.spent_since_reference_usd != null && <div className="text-xs text-slate-500 mt-2">Consumo Ellebierre dal riferimento: <strong>$ {Number(aiUsage.spent_since_reference_usd).toFixed(6)}</strong></div>}
            </div>
            <div className="flex flex-wrap gap-2">
              <button disabled={openaiTestBusy} onClick={async()=>{
                setOpenaiTestBusy(true);
                try {
                  const r = await api.testOpenai();
                  const u = await api.aiUsage();
                  setAiUsage(u);
                  if (r.ok) toast.success("OpenAI operativo");
                  else toast.error(r.error || "Test OpenAI non riuscito");
                } catch(e) { toast.error(e?.response?.data?.detail || "Test OpenAI non riuscito"); }
                finally { setOpenaiTestBusy(false); }
              }} className="px-3 py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold disabled:opacity-50">{openaiTestBusy ? "Verifica…" : "Verifica API"}</button>
              <a href={aiUsage.billing_url || "https://platform.openai.com/settings/organization/billing/overview"} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm font-semibold">Apri Billing OpenAI</a>
            </div>
          </div>
        </div>
        <div className="grid lg:grid-cols-2 gap-4 mt-4">
          <div className="border rounded-xl overflow-hidden"><div className="px-3 py-2 bg-slate-50 text-xs font-semibold">Per funzione</div>{(aiUsage.by_purpose||[]).length===0?<div className="p-4 text-sm text-slate-400">Nessun consumo registrato dopo l'attivazione del monitoraggio.</div>:(aiUsage.by_purpose||[]).map(x=><div key={x.name} className="px-3 py-2 border-t flex items-center text-sm"><span className="flex-1">{x.name}</span><span className="text-slate-500 mr-4">{x.calls} chiamate</span><strong>$ {Number(x.estimated_cost_usd||0).toFixed(4)}</strong></div>)}</div>
          <div className="border rounded-xl overflow-hidden"><div className="px-3 py-2 bg-slate-50 text-xs font-semibold">Per modello</div>{(aiUsage.by_model||[]).length===0?<div className="p-4 text-sm text-slate-400">I dati compariranno dalla prima chiamata OpenAI riuscita.</div>:(aiUsage.by_model||[]).map(x=><div key={x.name} className="px-3 py-2 border-t flex items-center text-sm"><span className="flex-1 font-mono text-xs">{x.name}</span><span className="text-slate-500 mr-4">{x.calls} chiamate</span><strong>$ {Number(x.estimated_cost_usd||0).toFixed(4)}</strong></div>)}</div>
        </div>
        <div className="mt-3 text-xs text-slate-500">La spesa Ellebierre è calcolata dai token effettivamente restituiti da OpenAI e dalle tariffe configurate per il modello. Il residuo è una stima rispetto al saldo di riferimento inserito; il saldo ufficiale resta quello mostrato nel portale Billing OpenAI.</div>
      </div>}

      {section === "azienda" && canSetting("azienda") && company && <fieldset disabled={!isAdmin} className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <BriefcaseBusiness className="w-5 h-5 text-orange-600" />
          <div>
            <div className="font-semibold text-slate-900">Azienda / Owner</div>
            <div className="text-sm text-slate-500">Anagrafica utilizzata nelle intestazioni e nei documenti generati.</div>
          </div>
        </div>
        <div className="mb-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-28 h-20 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
            {company.logo_file_id ? <img src={api.companyLogoUrl()} alt="Logo aziendale" className="max-w-full max-h-full object-contain" /> : <span className="text-xs text-slate-400">Nessun logo</span>}
          </div>
          <label className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer">
            {logoBusy ? "Caricamento…" : "Carica / sostituisci logo"}
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" disabled={logoBusy} onChange={async (e) => {
              const file = e.target.files?.[0]; if (!file) return;
              setLogoBusy(true);
              try { const saved = await api.uploadCompanyLogo(file); setCompany(saved); toast.success("Logo aziendale aggiornato"); }
              catch (err) { toast.error(err?.response?.data?.detail || "Caricamento logo non riuscito"); }
              finally { setLogoBusy(false); e.target.value = ""; }
            }} />
          </label>
          <div className="text-xs text-slate-500">PNG, JPG o WebP · max 5 MB. Verrà usato nelle intestazioni dei documenti.</div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            ["name","Ragione sociale"],["trade_name","Nome commerciale"],["vat","P.IVA"],["tax_code","Codice fiscale"],
            ["rea","REA"],["sdi","Codice SDI"],["pec","PEC"],["email","Email"],["phone","Telefono"],["website","Sito web"],
            ["address","Indirizzo"],["cap","CAP"],["city","Comune"],["province","Provincia"],["country","Paese"],
            ["representative_name","Referente / rappresentante"],["representative_role","Qualifica"],["representative_email","Email referente"],
            ["representative_phone","Telefono referente"],
          ].map(([key,label]) => <label key={key} className="text-sm">
            <span className="block font-medium text-slate-700 mb-1">{label}</span>
            <input value={company[key] || ""} onChange={(e) => setCompany({...company,[key]:e.target.value})}
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5" />
          </label>)}
        </div>
        <div className="mt-4 flex justify-end">
          <button disabled={companyBusy} onClick={async () => {
            setCompanyBusy(true);
            try {
              const saved = await api.updateCompanyProfile(company);
              setCompany(saved);
              toast.success("Anagrafica aziendale aggiornata");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Salvataggio anagrafica non riuscito");
            } finally { setCompanyBusy(false); }
          }} className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold px-4 py-2.5 rounded-lg disabled:opacity-50">
            <Save className="w-4 h-4" /> {companyBusy ? "Salvataggio…" : "Salva anagrafica"}
          </button>
        </div>
      </fieldset>}

      {section === "sicurezza" && isAdmin && <>
      <GoogleLoginSettings />
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <div className="font-semibold text-slate-900">Accesso amministrativo locale</div>
            <div className="text-sm text-slate-500 mt-1">Password di emergenza per accedere a Ellebierre senza Google.</div>
          </div>
          <button onClick={() => setShowPassword(true)} data-testid="settings-change-password"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold border border-slate-200 rounded-lg hover:bg-slate-50">
            <KeyRound className="w-4 h-4" /> Cambia password
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <div className="font-semibold text-slate-900">Account Google</div>
            <div className="text-sm text-slate-500 mt-1">
              {googleAccount?.connected ? googleAccount.email : "Nessun account Google collegato a questa sessione"}
            </div>
            {googleAccount?.connected && (
              <div className="flex flex-wrap gap-2 mt-3 text-xs">
                <span className="px-2 py-1 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">Google ✓</span>
                <span className={`px-2 py-1 rounded-full border ${googleAccount.gmail ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>Gmail {googleAccount.gmail ? "✓" : "—"}</span>
                <span className={`px-2 py-1 rounded-full border ${googleAccount.contacts ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>Contatti {googleAccount.contacts ? "✓" : "—"}</span>
                <span className={`px-2 py-1 rounded-full border ${googleAccount.calendar ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>Calendar {googleAccount.calendar ? "✓" : "—"}</span>
              </div>
            )}
          </div>
          {googleAccount?.connected ? (
            <div className="flex gap-2">
              <a href={authApi.googleLoginUrl()} className="px-3 py-2 text-sm font-semibold border border-slate-200 rounded-lg hover:bg-slate-50">Riautorizza Google</a>
              <button onClick={async () => {
                if (!window.confirm("Scollegare Google? Email, documenti e task già acquisiti resteranno nei cantieri.")) return;
                try {
                  await authApi.googleDisconnect();
                  window.location.href = "/";
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Scollegamento non riuscito");
                }
              }} className="px-3 py-2 text-sm font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50">Scollega Google</button>
            </div>
          ) : (
            <a href={authApi.googleLoginUrl()} className="px-4 py-2.5 text-sm font-semibold bg-slate-900 text-white rounded-lg hover:bg-slate-800">Collega Google</a>
          )}
        </div>
      </div>

      </>}

      {section === "integrazioni" && whatsapp && <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex flex-col md:flex-row gap-5">
          <div className="flex-1">
            <div className="font-semibold text-slate-900">WhatsApp Web · Canale ingresso cantiere</div>
            <div className="text-sm text-slate-500 mt-1">Sola lettura: Ellebierre acquisisce messaggi, vocali, foto, documenti e posizione. Nessun messaggio viene inviato dall'app.</div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${whatsapp.status==="ready"?"bg-emerald-50 text-emerald-700 border-emerald-200":whatsapp.status==="qr"?"bg-amber-50 text-amber-700 border-amber-200":whatsapp.status==="disabled"?"bg-red-50 text-red-700 border-red-200":"bg-slate-50 text-slate-600 border-slate-200"}`}>{whatsapp.status==="ready"?"Collegato":whatsapp.status==="qr"?"Scansiona QR":whatsapp.status==="disabled"?"Disabilitato":whatsapp.status==="offline"?"Servizio non raggiungibile":"Inizializzazione"}</span>
              <button disabled={whatsappToggleBusy} onClick={async()=>{
                const next=whatsapp.status==="disabled";
                setWhatsappToggleBusy(true);
                try {
                  await api.whatsappSetEnabled(next);
                  const fresh=await api.whatsappStatus();
                  setWhatsapp(fresh);
                  toast.success(next?"WhatsApp abilitato":"WhatsApp disabilitato");
                } catch(e) { toast.error(e?.response?.data?.detail || "Aggiornamento WhatsApp non riuscito"); }
                finally { setWhatsappToggleBusy(false); }
              }} className={`px-3 py-2 rounded-lg text-sm font-semibold border disabled:opacity-50 ${whatsapp.status==="disabled"?"bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700":"bg-red-50 text-red-700 border-red-200 hover:bg-red-100"}`}>
                {whatsappToggleBusy?"Aggiornamento…":whatsapp.status==="disabled"?"Abilita WhatsApp":"Disabilita WhatsApp"}
              </button>
            </div>
          </div>
          {whatsapp.status==="qr" && <WhatsAppQr onReady={()=>api.whatsappStatus().then(setWhatsapp)}/>}
        </div>

        {whatsapp.status==="ready" && <div className="mt-5 border-t border-slate-100 pt-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-800">Chat WhatsApp selezionate</div>
              <div className="text-xs text-slate-500">Ogni chat può essere associata a più cantieri e/o alle Comunicazioni società.</div>
            </div>
            <button disabled={whatsappSyncBusy} onClick={async()=>{
              setWhatsappSyncBusy(true);
              try {
                const r=await api.whatsappSync();
                toast.success("WhatsApp: "+r.new+" nuovi · "+r.needs_review+" da verificare · "+r.errors+" errori");
                await loadWhatsappChannels();
              } catch(e) { toast.error(e?.response?.data?.detail || "Sincronizzazione WhatsApp non riuscita"); }
              finally { setWhatsappSyncBusy(false); }
            }} className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50">
              {whatsappSyncBusy ? "Sincronizzo…" : "Sincronizza ora"}
            </button>
          </div>

          <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
            {whatsappChannels.length===0 && <div className="p-4 text-sm text-slate-400">Nessuna chat disponibile.</div>}
            {whatsappChannels.map(ch=><div key={ch.chat_id} className="p-3 grid lg:grid-cols-[minmax(0,1fr)_minmax(320px,1.3fr)_auto] gap-3 items-start">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-800 truncate">{ch.chat_name || ch.chat_id}</div>
                <div className="text-[11px] text-slate-400 truncate">{(ch.is_group ? "Gruppo" : "Chat")+" · "+ch.chat_id}</div>
                {ch.last_sync && <div className="text-[11px] text-slate-400 mt-1">{"Ultima sync: "+new Date(ch.last_sync).toLocaleString("it-IT")+" · nuovi "+(ch.last_stats?.new||0)+" · da verificare "+(ch.last_stats?.needs_review||0)}</div>}
              </div>
              <div className="border border-slate-200 rounded-lg p-2.5 space-y-2 max-h-44 overflow-y-auto">
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                  <input type="checkbox" checked={!!ch.company_communications} onChange={async(e)=>{
                    const payload={site_ids:ch.site_ids||[],company_communications:e.target.checked,enabled:e.target.checked||(ch.site_ids||[]).length>0,include_history:false};
                    try{await api.whatsappMapChannel(ch.chat_id,payload);await loadWhatsappChannels();}catch(err){toast.error(err?.response?.data?.detail||"Aggiornamento non riuscito")}
                  }}/>
                  Comunicazioni società
                </label>
                <div className="border-t border-slate-100 pt-2 space-y-1">
                  {sites.map(site=><label key={site.id} className="flex items-center gap-2 text-xs text-slate-600">
                    <input type="checkbox" checked={(ch.site_ids||[]).includes(site.id)} onChange={async(e)=>{
                      const ids=e.target.checked?[...(ch.site_ids||[]),site.id]:(ch.site_ids||[]).filter(x=>x!==site.id);
                      const payload={site_ids:[...new Set(ids)],company_communications:!!ch.company_communications,enabled:ids.length>0||!!ch.company_communications,include_history:false};
                      try{await api.whatsappMapChannel(ch.chat_id,payload);await loadWhatsappChannels();}catch(err){toast.error(err?.response?.data?.detail||"Aggiornamento non riuscito")}
                    }}/>
                    <span className="truncate">{site.name}</span>
                  </label>)}
                </div>
              </div>
              <label className="inline-flex items-center gap-2 text-xs text-slate-600 pt-2">
                <input type="checkbox" checked={!!ch.enabled && ((ch.site_ids||[]).length>0||!!ch.company_communications)} disabled={!((ch.site_ids||[]).length>0||!!ch.company_communications)} onChange={async(e)=>{
                  try {
                    await api.whatsappMapChannel(ch.chat_id,{site_ids:ch.site_ids||[],company_communications:!!ch.company_communications,enabled:e.target.checked,include_history:false});
                    await loadWhatsappChannels();
                  } catch(err) { toast.error(err?.response?.data?.detail || "Aggiornamento non riuscito"); }
                }}/>
                Acquisizione
              </label>
            </div>)}
          </div>

          <div className="mt-3 grid sm:grid-cols-4 gap-2 text-xs">
            <div className="rounded-lg bg-orange-50 text-orange-800 px-3 py-2"><strong>Rapportini</strong><br/>→ Giornale lavori</div>
            <div className="rounded-lg bg-blue-50 text-blue-800 px-3 py-2"><strong>DDT</strong><br/>→ Contabilità</div>
            <div className="rounded-lg bg-violet-50 text-violet-800 px-3 py-2"><strong>Fatture</strong><br/>→ Contabilità</div>
            <div className="rounded-lg bg-emerald-50 text-emerald-800 px-3 py-2"><strong>Presenze</strong><br/>→ Registro operai</div>
          </div>
        </div>}
      </div>}

      {section === "utenti" && canSetting("utenti") && <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <Users className="w-5 h-5 text-slate-500" />
          <span className="font-semibold text-slate-900">
            Utenti autorizzati
          </span>
          <span className="ml-auto text-xs text-slate-400">
            {users.length} utenti
          </span>
        </div>

        {users.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">
            Nessun utente configurato. L'accesso amministrativo tramite password
            continua a funzionare.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {users.map((u) => (
              <div
                key={u.id}
                data-testid={`user-row-${u.id}`}
                className="p-4 flex flex-col lg:flex-row lg:items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900">
                    {u.name || u.email}
                  </div>
                  <div className="text-sm text-slate-500 truncate">
                    {u.email}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 text-xs">
                  {u.is_admin && (
                    <span className="px-2 py-1 rounded-full bg-orange-50 text-orange-700 border border-orange-200">
                      Admin
                    </span>
                  )}

                  <span
                    className={`px-2 py-1 rounded-full border ${
                      u.active
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-slate-100 text-slate-500 border-slate-200"
                    }`}
                  >
                    {u.active ? "Attivo" : "Disattivato"}
                  </span>

                  <span className="px-2 py-1 rounded-full bg-slate-50 text-slate-600 border border-slate-200">
                    {u.all_sites
                      ? "Tutti i cantieri"
                      : `${u.site_ids?.length || 0} cantieri`}
                  </span>

                  <span className="px-2 py-1 rounded-full bg-slate-50 text-slate-600 border border-slate-200">
                    Google {u.google_connected ? "collegato" : "da collegare"}
                  </span>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => openEdit(u)}
                    className="px-3 py-2 text-sm font-semibold border border-slate-200 rounded-lg hover:bg-slate-50"
                  >
                    Gestisci
                  </button>

                  {u.active && (
                    <button
                      onClick={() => disable(u)}
                      className="p-2 text-red-500 border border-red-100 rounded-lg hover:bg-red-50"
                      title="Disattiva"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>}

      {showPassword && <ChangePasswordModal onClose={() => setShowPassword(false)} />}

      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center">
              <div>
                <div className="font-display font-bold text-slate-900">
                  {editing.id ? "Gestisci utente" : "Autorizza utente"}
                </div>
                <div className="text-xs text-slate-500">
                  L'email dovrà corrispondere all'account Google utilizzato.
                </div>
              </div>

              <button
                onClick={() => setEditing(null)}
                className="ml-auto p-1.5 text-slate-400 hover:text-slate-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-6">
              <div>
                <div className="font-semibold text-sm text-slate-800 mb-2">Profilo utente</div>
                <div className="grid sm:grid-cols-4 gap-2">
                  {Object.entries(ROLE_PRESETS).map(([key, preset]) => (
                    <button key={key} type="button" onClick={() => setEditing((u) => ({ ...u, role: key, is_admin: preset.is_admin, all_sites: preset.all_sites, permissions: { ...preset.permissions } }))}
                      className={`px-3 py-2 rounded-lg border text-xs font-semibold ${editing.role === key ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <label className="text-sm">
                  <span className="block font-medium text-slate-700 mb-1">
                    Email Google
                  </span>
                  <input
                    type="email"
                    disabled={!!editing.id}
                    value={editing.email}
                    onChange={(e) =>
                      setEditing({ ...editing, email: e.target.value })
                    }
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 disabled:bg-slate-50"
                    placeholder="utente@gmail.com"
                  />
                </label>

                <label className="text-sm">
                  <span className="block font-medium text-slate-700 mb-1">
                    Nome
                  </span>
                  <input
                    value={editing.name}
                    onChange={(e) =>
                      setEditing({ ...editing, name: e.target.value })
                    }
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5"
                    placeholder="Nome e cognome"
                  />
                </label>
              </div>

              <div className="flex flex-wrap gap-5">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={editing.active}
                    onChange={(e) =>
                      setEditing({ ...editing, active: e.target.checked })
                    }
                  />
                  Utente attivo
                </label>

                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={editing.is_admin}
                    onChange={(e) =>
                      setEditing({ ...editing, is_admin: e.target.checked })
                    }
                  />
                  Amministratore
                </label>

                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={editing.all_sites}
                    onChange={(e) =>
                      setEditing({ ...editing, all_sites: e.target.checked })
                    }
                  />
                  Tutti i cantieri
                </label>
              </div>

              {!editing.all_sites && (
                <div>
                  <div className="font-semibold text-sm text-slate-800 mb-2">
                    Cantieri accessibili
                  </div>

                  <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-48 overflow-y-auto">
                    {sites.map((s) => (
                      <label
                        key={s.id}
                        className="flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-slate-50"
                      >
                        <input
                          type="checkbox"
                          checked={editing.site_ids.includes(s.id)}
                          onChange={() => toggleSite(s.id)}
                        />
                        <span>{s.name}</span>
                      </label>
                    ))}

                    {sites.length === 0 && (
                      <div className="p-3 text-sm text-slate-400">
                        Nessun cantiere disponibile.
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="border border-slate-200 rounded-xl p-4 space-y-3" data-testid="granular-menu-permissions">
                <div className="font-semibold text-sm text-slate-800">Accessi e autorizzazioni</div>
                <p className="text-xs text-slate-500">I sottomenu ereditano il permesso dell'area, salvo eccezioni. I controlli sono applicati anche dalle API.</p>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" disabled={editing.is_admin} checked={editing.permissions?.write !== false}
                    onChange={e=>setEditing(u=>({...u,permissions:{...u.permissions,write:e.target.checked}}))}/>
                  Consenti modifiche nelle aree autorizzate
                </label>
                <div className="space-y-2">
                  {MENU_PERMISSIONS.map(area => {
                    const control=(sub,label)=>{
                      const key=menuKey(area.key,sub);
                      return <label key={key} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                        <span>{label}</span>
                        <select aria-label={"Accesso "+area.label+" / "+label} disabled={editing.is_admin}
                          value={permissionChoice(editing.permissions,key)}
                          onChange={e=>setEditing(u=>({...u,permissions:withPermissionChoice(u.permissions,key,e.target.value)}))}
                          className="border rounded px-2 py-1.5 bg-white">
                          <option value="inherit">Eredita</option><option value="grant">Consenti</option><option value="deny">Nega</option>
                        </select>
                        <span className="text-xs text-slate-500">{canMenu(editing,area.key,sub) ? "Accessibile" : "Non accessibile"}</span>
                      </label>;
                    };
                    const exceptions=(area.children||[]).filter(([sub])=>permissionChoice(editing.permissions,menuKey(area.key,sub))!=="inherit").length;
                    return <div key={area.key} className="rounded-lg border border-slate-100 p-3 space-y-2">
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="flex-1 min-w-32 font-medium text-sm">{area.label}</div>
                        <select aria-label={"Accesso "+area.label} disabled={editing.is_admin}
                          value={permissionChoice(editing.permissions,menuKey(area.key))}
                          onChange={e=>setEditing(u=>({...u,permissions:withPermissionChoice(u.permissions,menuKey(area.key),e.target.value)}))}
                          className="border rounded px-2 py-1.5 bg-white text-sm">
                          <option value="inherit">Eredita</option><option value="grant">Consenti</option><option value="deny">Nega</option>
                        </select>
                        <span className="text-xs text-slate-500">{canMenu(editing,area.key) ? "Accessibile" : "Non accessibile"}</span>
                      </div>
                      {!!area.children?.length && <details>
                        <summary className="cursor-pointer text-xs text-slate-600">Sottomenu ({area.children.length}){exceptions ? " - "+exceptions+" eccezioni" : ""}</summary>
                        <div className="grid grid-cols-1 gap-2 pl-4 pt-3">{area.children.map(([sub,label])=>control(sub,label))}</div>
                      </details>}
                    </div>;
                  })}
                </div>
              </div>

              <button
                onClick={save}
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 rounded-xl disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {busy ? "Salvataggio…" : "Salva utente"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
